import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { patientApi } from '@/api/patient';
import { useSearch } from '@/contexts/SearchContext';
import { FileArchive, Loader2, Pencil } from 'lucide-react';
import QRCode from 'qrcode';

interface Hospital {
  id: string;
  name: string;
  address: string;
  contactNumber: string;
  email: string;
  gstin?: string;
  /** Optional Google Maps / directions URL used in WhatsApp messages */
  mapsUrl?: string;
  /** Google Place ID for GMB rating (Places API). */
  googlePlaceId?: string | null;
  ipdFeedbackSheetUrl?: string | null;
  postSurgeryContact1Name?: string | null;
  postSurgeryContact1Phone?: string | null;
  postSurgeryContact2Name?: string | null;
  postSurgeryContact2Phone?: string | null;
  status: 'ACTIVE' | 'INACTIVE';
}

export default function Hospitals() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [qrHospital, setQrHospital] = useState<Hospital | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [exportingHospitalId, setExportingHospitalId] = useState<string | null>(null);
  const { searchQuery } = useSearch();
  const queryClient = useQueryClient();

  const { data: hospitals, isLoading } = useQuery<Hospital[]>({
    queryKey: ['hospitals'],
    queryFn: async () => {
      const response = await api.get('/api/super-admin/hospital/fetch-all');
      return response.data?.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Hospital, 'id' | 'status'>) => {
      const response = await api.post('/api/super-admin/hospital/create', data);
      return response.data?.data as Hospital;
    },
    onSuccess: (hospital) => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      setIsAddDialogOpen(false);
      toast.success('Hospital created successfully');
      setQrHospital(hospital);
      setIsQrDialogOpen(true);
    },
    onError: (error) => {
      toast.error('Failed to create hospital');
      console.error('Error creating hospital:', error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Hospital> & { id: string }) => {
      const response = await api.patch(`/api/super-admin/hospital/update/${data.id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      setIsEditDialogOpen(false);
      toast.success('Hospital updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update hospital');
      console.error('Error updating hospital:', error);
    },
  });

  const downloadHospitalPatientCsv = async (hospital: Hospital) => {
    setExportingHospitalId(hospital.id);
    try {
      const res = await patientApi.downloadHospitalPatientHistoryCsv(hospital.id);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = hospital.name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 50);
      a.download = `hospital-${slug}-patients-csv.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Patient CSV bundle for ${hospital.name} downloaded`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: Blob } };
      let msg = 'Export failed';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text) as { message?: string };
          if (j?.message) msg = j.message;
        } catch {
          /* ignore */
        }
      }
      toast.error(msg);
    } finally {
      setExportingHospitalId(null);
    }
  };

  const toggleHospitalStatus = (hospital: Hospital) => {
    const newStatus = hospital.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    if (window.confirm(`Are you sure you want to ${hospital.status === 'ACTIVE' ? 'disable' : 'enable'} this hospital?`)) {
      updateMutation.mutate({
        id: hospital.id,
        status: newStatus
      });
    }
  };

  const handleAddSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createMutation.mutate({
      name: formData.get('name') as string,
      address: formData.get('address') as string,
      contactNumber: formData.get('contactNumber') as string,
      email: formData.get('email') as string,
      gstin: (formData.get('gstin') as string) || undefined,
      mapsUrl: (formData.get('mapsUrl') as string) || undefined,
      googlePlaceId: (formData.get('googlePlaceId') as string) || undefined,
      ipdFeedbackSheetUrl: (formData.get('ipdFeedbackSheetUrl') as string) || undefined,
      postSurgeryContact1Name: (formData.get('postSurgeryContact1Name') as string) || undefined,
      postSurgeryContact1Phone: (formData.get('postSurgeryContact1Phone') as string) || undefined,
      postSurgeryContact2Name: (formData.get('postSurgeryContact2Name') as string) || undefined,
      postSurgeryContact2Phone: (formData.get('postSurgeryContact2Phone') as string) || undefined,
    });
  };

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedHospital) return;

    const formData = new FormData(e.currentTarget);
    updateMutation.mutate({
      id: selectedHospital.id,
      name: formData.get('name') as string,
      address: formData.get('address') as string,
      contactNumber: formData.get('contactNumber') as string,
      email: formData.get('email') as string,
      gstin: (formData.get('gstin') as string) || undefined,
      mapsUrl: (formData.get('mapsUrl') as string) || undefined,
      googlePlaceId: (formData.get('googlePlaceId') as string) || undefined,
      ipdFeedbackSheetUrl: (formData.get('ipdFeedbackSheetUrl') as string) || undefined,
      postSurgeryContact1Name: (formData.get('postSurgeryContact1Name') as string) || undefined,
      postSurgeryContact1Phone: (formData.get('postSurgeryContact1Phone') as string) || undefined,
      postSurgeryContact2Name: (formData.get('postSurgeryContact2Name') as string) || undefined,
      postSurgeryContact2Phone: (formData.get('postSurgeryContact2Phone') as string) || undefined,
    });
  };

  const filteredHospitals = hospitals?.filter((hospital: Hospital) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      hospital.name.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    async function generate() {
      if (!isQrDialogOpen || !qrHospital?.id) return;
      const link = `${window.location.origin.replace(/\/+$/, '')}/self-register?h=${encodeURIComponent(qrHospital.id)}`;
      const dataUrl = await QRCode.toDataURL(link, { margin: 1, width: 320 });
      setQrDataUrl(dataUrl);
    }
    generate().catch((e) => {
      console.error('Failed to generate QR', e);
      setQrDataUrl('');
    });
  }, [isQrDialogOpen, qrHospital?.id]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Hospital</h1>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="text-white bg-blue-800 hover:bg-blue-700"
              disabled={createMutation.isPending}
            >
              Add Hospital
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Hospital</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className=" font-semibold">Name</Label>
                <Input id="name" name="name" required />
              </div>
              <div>
                <Label htmlFor="address" className="font-semibold">Address</Label>
                <Input id="address" name="address" required />
              </div>
              <div>
                <Label htmlFor="contactNumber" className=" font-semibold">Contact Number</Label>
                <Input
                  id="contactNumber"
                  name="contactNumber"
                  required
                  pattern="[0-9]{10}"
                  maxLength={10}
                  title="Please enter a valid 10-digit phone number"
                  onKeyPress={(e) => {
                    // Allow only numbers
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Remove any non-numeric characters
                    e.target.value = e.target.value.replace(/\D/g, '');
                    // Limit to 10 digits
                    if (e.target.value.length > 10) {
                      e.target.value = e.target.value.slice(0, 10);
                    }
                  }}
                />
                <p className="text-xs text-artis-slate mt-1">Enter a 10-digit phone number</p>
              </div>
              <div>
                <Label htmlFor="email" className=" font-semibold">Email</Label>
                <Input id="email" name="email" type="email" required />
              </div>
              <div>
                <Label htmlFor="gstin" className="font-semibold">GSTIN</Label>
                <Input
                  id="gstin"
                  name="gstin"
                  placeholder="e.g. 07ABCCA6081B1Z1"
                  maxLength={15}
                />
                <p className="text-xs text-artis-slate mt-1">Optional. Shown on hospital bills.</p>
              </div>
              <div>
                <Label htmlFor="mapsUrl" className="font-semibold">Google Maps URL (Get Directions)</Label>
                <Input
                  id="mapsUrl"
                  name="mapsUrl"
                  type="url"
                  placeholder="https://maps.app.goo.gl/..."
                />
                <p className="text-xs text-artis-slate mt-1">
                  Optional. Paste the exact link from Google Maps to use in WhatsApp \"Get Directions\".
                </p>
              </div>
              <div>
                <Label htmlFor="googlePlaceId" className="font-semibold">Google Place ID (GMB rating)</Label>
                <Input
                  id="googlePlaceId"
                  name="googlePlaceId"
                  placeholder="ChIJ..."
                />
                <p className="text-xs text-artis-slate mt-1">
                  Optional. From Google Maps (share link or Place ID) for Business Reports GMB rating.
                </p>
              </div>
              <div>
                <Label htmlFor="ipdFeedbackSheetUrl" className="font-semibold">IPD feedback Google Sheet (NPS)</Label>
                <Input
                  id="ipdFeedbackSheetUrl"
                  name="ipdFeedbackSheetUrl"
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                />
                <p className="text-xs text-artis-slate mt-1">
                  Optional. IPD form responses — link-view share. Customer Voice and Business Reports NPS.
                </p>
              </div>
              <div className="rounded-md border p-3 space-y-3">
                <div>
                  <Label className="font-semibold">Post Surgery contact points</Label>
                  <p className="text-xs text-artis-slate mt-1">
                    Optional. Shown at the end of the IPD discharge summary PDF.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="postSurgeryContact1Name">Contact 1 — name</Label>
                    <Input id="postSurgeryContact1Name" name="postSurgeryContact1Name" />
                  </div>
                  <div>
                    <Label htmlFor="postSurgeryContact1Phone">Contact 1 — phone</Label>
                    <Input id="postSurgeryContact1Phone" name="postSurgeryContact1Phone" inputMode="tel" />
                  </div>
                  <div>
                    <Label htmlFor="postSurgeryContact2Name">Contact 2 — name</Label>
                    <Input id="postSurgeryContact2Name" name="postSurgeryContact2Name" />
                  </div>
                  <div>
                    <Label htmlFor="postSurgeryContact2Phone">Contact 2 — phone</Label>
                    <Input id="postSurgeryContact2Phone" name="postSurgeryContact2Phone" inputMode="tel" />
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full">
                Add Hospital
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* QR dialog after hospital creation */}
      <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Self Registration QR (Hospital)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Hospital: <span className="font-medium text-foreground">{qrHospital?.name ?? '-'}</span>
            </div>

            {qrDataUrl ? (
              <div className="border rounded-md p-4 bg-white inline-block">
                <img src={qrDataUrl} alt="Self registration QR" className="w-64 h-64" />
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Generating QR...</div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  if (!qrHospital?.id || !qrDataUrl) return;
                  const link = `${window.location.origin.replace(/\/+$/, '')}/self-register?h=${encodeURIComponent(qrHospital.id)}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    toast.success('Link copied');
                  } catch {
                    toast.error('Copy failed');
                  }
                }}
                disabled={!qrHospital?.id}
              >
                Copy link
              </Button>
              <Button
                onClick={() => {
                  if (!qrHospital?.id || !qrDataUrl) return;
                  const a = document.createElement('a');
                  a.href = qrDataUrl;
                  a.download = `self-register-qr-${qrHospital.id}.png`;
                  a.click();
                }}
                disabled={!qrHospital?.id}
              >
                Download QR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-card p-4 text-sm space-y-2">
        <p className="font-semibold text-base">Patient records (hospital-wide)</p>
        <p className="text-muted-foreground">
          Download a ZIP file of UTF-8 CSV sheets: demographics, appointments, vitals, lab results, bills, payments,
          prescriptions, IPD, day care, insurance, and document metadata for <strong>all patients</strong> in each
          hospital. Use row actions below. Very large exports may take longer to generate.
        </p>
      </div>

      <div>
        <Table numberOfRows={9}>
          <TableHeader>
            <TableRow >
              <TableHead>Hospitals Name</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Mobile Number</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Maps URL</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Patient CSV</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredHospitals?.map((hospital) => (
              <TableRow key={hospital.id}>
                <TableCell>
                  <div className="font-medium">{hospital.name}</div>
                  <div
                    className="text-xs text-gray-500 font-mono select-all break-all mt-0.5"
                    title="Hospital ID — use for APIs (e.g. leads ingest, self-register)"
                  >
                    ID: {hospital.id}
                  </div>
                </TableCell>
                <TableCell>{hospital.address}</TableCell>
                <TableCell>{hospital.email}</TableCell>
                <TableCell>{hospital.contactNumber}</TableCell>
                <TableCell>{hospital.gstin || '—'}</TableCell>
                <TableCell>
                  {hospital.mapsUrl ? (
                    <a
                      href={hospital.mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Open
                    </a>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex px-2 py-1 rounded-full text-xs 
                      font-medium ${hospital.status === 'ACTIVE'
                        ? 'bg-green-50 text-green-600'
                        : 'bg-red-50 text-red-600'
                      }`}
                  >

                    {hospital.status}
                  </span>
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={exportingHospitalId === hospital.id}
                    onClick={() => downloadHospitalPatientCsv(hospital)}
                  >
                    {exportingHospitalId === hospital.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileArchive className="h-3.5 w-3.5" />
                    )}
                    ZIP / CSV
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setQrHospital(hospital);
                        setIsQrDialogOpen(true);
                      }}
                      className="px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      View QR
                    </button>
                    <button
                      onClick={() => {
                        setSelectedHospital(hospital);
                        setIsEditDialogOpen(true);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <Pencil className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => toggleHospitalStatus(hospital)}
                      className={`px-2 py-1 rounded-md text-xs font-medium ${hospital.status === 'ACTIVE'
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                        }`}
                    >
                      {hospital.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Hospital</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                defaultValue={selectedHospital?.name}
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                defaultValue={selectedHospital?.address}
                required
              />
            </div>
            <div>
              <Label htmlFor="contactNumber">Contact Number</Label>
              <Input
                id="contactNumber"
                name="contactNumber"
                defaultValue={selectedHospital?.contactNumber}
                required
                pattern="[0-9]{10}"
                maxLength={10}
                title="Please enter a valid 10-digit phone number"
                onKeyPress={(e) => {
                  // Allow only numbers
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  // Remove any non-numeric characters
                  e.target.value = e.target.value.replace(/\D/g, '');
                  // Limit to 10 digits
                  if (e.target.value.length > 10) {
                    e.target.value = e.target.value.slice(0, 10);
                  }
                }}
              />
              <p className="text-xs text-artis-slate mt-1">Enter a 10-digit phone number</p>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                defaultValue={selectedHospital?.email}
                required
              />
            </div>
            <div>
              <Label htmlFor="gstin">GSTIN</Label>
              <Input
                id="gstin"
                name="gstin"
                placeholder="e.g. 07ABCCA6081B1Z1"
                defaultValue={selectedHospital?.gstin ?? ''}
                maxLength={15}
              />
              <p className="text-xs text-artis-slate mt-1">Optional. Shown on hospital bills.</p>
            </div>
            <div>
              <Label htmlFor="mapsUrl">Google Maps URL (Get Directions)</Label>
              <Input
                id="mapsUrl"
                name="mapsUrl"
                type="url"
                defaultValue={selectedHospital?.mapsUrl || ''}
                placeholder="https://maps.app.goo.gl/..."
              />
              <p className="text-xs text-artis-slate mt-1">
                Optional. Paste the exact link from Google Maps to use in WhatsApp \"Get Directions\".
              </p>
            </div>
            <div>
              <Label htmlFor="googlePlaceId">Google Place ID (GMB rating)</Label>
              <Input
                id="googlePlaceId"
                name="googlePlaceId"
                defaultValue={selectedHospital?.googlePlaceId || ''}
                placeholder="ChIJ..."
              />
              <p className="text-xs text-artis-slate mt-1">
                Optional. Used for Business Reports Summary GMB rating via Places API.
              </p>
            </div>
            <div>
              <Label htmlFor="ipdFeedbackSheetUrl">IPD feedback Google Sheet (NPS)</Label>
              <Input
                id="ipdFeedbackSheetUrl"
                name="ipdFeedbackSheetUrl"
                type="url"
                defaultValue={selectedHospital?.ipdFeedbackSheetUrl || ''}
                placeholder="https://docs.google.com/spreadsheets/d/..."
              />
              <p className="text-xs text-artis-slate mt-1">
                Optional. IPD form responses sheet (link-view share) for Customer Voice / NPS.
              </p>
            </div>
            <div className="rounded-md border p-3 space-y-3">
              <div>
                <Label className="font-semibold">Post Surgery contact points</Label>
                <p className="text-xs text-artis-slate mt-1">
                  Optional. Shown at the end of the IPD discharge summary PDF.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="postSurgeryContact1Name">Contact 1 — name</Label>
                  <Input
                    id="postSurgeryContact1Name"
                    name="postSurgeryContact1Name"
                    defaultValue={selectedHospital?.postSurgeryContact1Name ?? ''}
                  />
                </div>
                <div>
                  <Label htmlFor="postSurgeryContact1Phone">Contact 1 — phone</Label>
                  <Input
                    id="postSurgeryContact1Phone"
                    name="postSurgeryContact1Phone"
                    inputMode="tel"
                    defaultValue={selectedHospital?.postSurgeryContact1Phone ?? ''}
                  />
                </div>
                <div>
                  <Label htmlFor="postSurgeryContact2Name">Contact 2 — name</Label>
                  <Input
                    id="postSurgeryContact2Name"
                    name="postSurgeryContact2Name"
                    defaultValue={selectedHospital?.postSurgeryContact2Name ?? ''}
                  />
                </div>
                <div>
                  <Label htmlFor="postSurgeryContact2Phone">Contact 2 — phone</Label>
                  <Input
                    id="postSurgeryContact2Phone"
                    name="postSurgeryContact2Phone"
                    inputMode="tel"
                    defaultValue={selectedHospital?.postSurgeryContact2Phone ?? ''}
                  />
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                if (!selectedHospital) return;
                setQrHospital(selectedHospital);
                setIsQrDialogOpen(true);
              }}
            >
              View Self Registration QR
            </Button>
            <Button type="submit" className="w-full">
              Update Hospital
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
} 